import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, type TemplateRef } from '@angular/core';
import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectionSummaryPanelComponent } from './selection-summary-panel.component';
import { BulkEditPropertyDialogComponent } from './bulk-edit-property-dialog.component';
import { BulkEditJobDetailsService } from './bulk-edit-job-details.service';
import type { BulkEditSection } from './bulk-edit-section.types';
import type { BulkSelectionPayload } from './bulk-edit.models';
import type { BulkEditPropertyDialogData } from './bulk-edit-inline.types';

export interface BulkEditDialogData {
  sections: BulkEditSection[];
  resourcePath: string;
  collectionKey: string;
  selectedCount: number;
  selectionPayload: BulkSelectionPayload;
  bulkJobId?: string;
  entityNameResolver?: (id: string) => string | undefined;
  entityResolver?: (id: string) => unknown;
  resultRowTemplate?: TemplateRef<{ $implicit: unknown }>;
}

@Component({
  selector: 'zag-bulk-edit-dialog',
  templateUrl: './bulk-edit-dialog.component.html',
  styleUrl: './bulk-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectionSummaryPanelComponent],
})
export class BulkEditDialogComponent implements OnInit {
  protected readonly dialogRef = inject<DialogRef<void>>(DialogRef);
  protected readonly data = inject<BulkEditDialogData>(DIALOG_DATA);

  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly jobDetailsService = inject(BulkEditJobDetailsService);

  ngOnInit(): void {
    if (this.data.bulkJobId) {
      this.jobDetailsService.openById(this.data.resourcePath, this.data.bulkJobId);
      this.dialogRef.close();
    }
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected onSectionSelected(section: BulkEditSection): void {
    const propertyData: BulkEditPropertyDialogData = {
      section,
      selectedCount: this.data.selectedCount,
      selectionPayload: this.data.selectionPayload,
      resourcePath: this.data.resourcePath,
    };

    this.dialog
      .open(BulkEditPropertyDialogComponent, {
        data: propertyData,
        panelClass: ['zag-dialog-panel', 'zag-dialog-panel--sm'],
        backdropClass: 'zag-dialog-backdrop',
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close());
  }
}
